/**
 * Union Tool Dialog
 *
 * Material-UI dialog for configuring union operations.
 * Supports multi-layer selection for combining features.
 *
 * **Validates: Requirements 24.1**
 */

import React, { useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState, AppDispatch } from '../../store';
import { toggleUnionLayer, closeTool } from '../../store/slices/spatialAnalysisSlice';

interface UnionToolDialogProps {
  open: boolean;
  onExecute: () => void;
}

export const UnionToolDialog: React.FC<UnionToolDialogProps> = ({ open, onExecute }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { unionTool, isProcessing, error, message } = useSelector(
    (state: RootState) => state.spatialAnalysis
  );
  const layers = useSelector((state: RootState) => state.layers.layers);

  const handleClose = useCallback(() => {
    dispatch(closeTool());
  }, [dispatch]);

  const handleToggle = useCallback(
    (layerId: string) => {
      dispatch(toggleUnionLayer(layerId));
    },
    [dispatch]
  );

  const canExecute = unionTool.selectedLayerIds.length >= 2 && !isProcessing;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Union Operation</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Combine features from multiple layers into a single layer. Select at least two layers to
            union.
          </Typography>

          <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
            {layers.map((layer) => (
              <ListItem key={layer.id} disablePadding>
                <ListItemButton onClick={() => handleToggle(layer.id)} dense>
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={unionTool.selectedLayerIds.includes(layer.id)}
                      tabIndex={-1}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={layer.name}
                    secondary={`${layer.geometryType || 'Unknown'} • ${layer.featureCount || 0} features`}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          <Typography variant="caption" color="text.secondary">
            {unionTool.selectedLayerIds.length} layer(s) selected
          </Typography>

          {error && <Alert severity="error">{error}</Alert>}
          {message && !error && <Alert severity="success">{message}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isProcessing}>
          Cancel
        </Button>
        <Button
          onClick={onExecute}
          variant="contained"
          disabled={!canExecute}
          startIcon={isProcessing ? <CircularProgress size={16} /> : undefined}
        >
          {isProcessing ? 'Processing...' : 'Create Union'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UnionToolDialog;
